import type { Definition, OptionalView, RecipeView } from '../api/missions';
import { ca } from '../i18n/ca';
import {
  doWords,
  optionalOn,
  placeWords,
  sceneLabel,
  scriptedForceIds,
  whenWords,
} from './scriptWords';
import { settingInDefinition } from './recipeSettings';

const s = ca.script;

/**
 * The designer's *Guió* panel (docs/design/ENGINE_DESIGN.md §2.20, engine step
 * E12b): a scripted recipe's places, scenes, forces and people, mechanics,
 * phases, clock and end, read from the definition and never written — phases
 * stay the generator's (the owner's decision 6, 2026-09-27). The one control
 * is the recipe's own options, each a toggle that generates the mission again
 * through `onToggle`, never a hand edit of the script.
 *
 * Only whoever may already see the order of battle has a `script` to show:
 * the platform strips it whole from everybody else's copy (MissionService.maskHidden,
 * MECHANICS.md R16), and the designer shows this panel only when it is there.
 */
export function ScriptPanel({
  def,
  recipe,
  busy,
  onToggle,
  onSetting,
  onMove,
}: {
  def: Definition;
  recipe: RecipeView | null;
  busy: boolean;
  onToggle: (optional: OptionalView) => void;
  /**
   * One of the type's own settings changed (release D, D6.3): the mission is
   * generated again with it, as a toggle of an own option is.
   */
  onSetting?: (id: string, value: string) => void;
  /**
   * «Mou» on one of the places the generator answered (release D, D11.8):
   * a script's place, the base or the way out, moved among its kind's own
   * candidates by the designer. Absent when the mission cannot be edited.
   */
  onMove?: (id: string, label: string) => void;
}) {
  const script = def.script;
  if (!script) return null;
  const scripted = scriptedForceIds(def);
  const forces = (def.forces ?? []).filter((f) => scripted.has(f.id));
  const people = (def.cast ?? []).filter((c) => c.scene || c.sceneRole || c.mechanic);
  const own = (recipe?.optionals ?? []).filter((o) => o.scope === 'own');
  const settings = recipe?.settings ?? [];
  const phases = script.phases ?? [];
  const limit = def.time?.limitMinutes ?? recipe?.minutes ?? null;
  const ownClock = script.clock && script.clock.generic !== true;
  // The points a pin may move: the base, and the way out unless it is the base.
  const points = [
    ...(def.points?.base?.position ? ['point:base'] : []),
    ...(def.points?.extraction?.position && !def.points.extraction.atBase
      ? ['point:extraction']
      : []),
  ];

  return (
    <section className="card script-panel" aria-label={s.panel}>
      <h2>{s.panel}</h2>
      <p className="muted small">{s.hint}</p>

      <h3>{s.own}</h3>
      {own.length === 0 ? (
        <p className="muted small">{s.ownNone}</p>
      ) : (
        <>
          <p className="muted small">{s.ownHint}</p>
          <ul className="plain stack">
            {own.map((o) => {
              const on = optionalOn(o, def);
              return (
                <li key={o.key}>
                  <label className="check">
                    <input
                      type="checkbox"
                      name={`own-${o.key}`}
                      checked={on}
                      disabled={busy}
                      onChange={() => onToggle(o)}
                    />
                    <span>
                      <strong>{o.title}</strong>
                      <span className="muted small block">{o.blurb}</span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
          {busy && (
            <p className="muted small" role="status">
              {s.regenerating}
            </p>
          )}
        </>
      )}

      {settings.length > 0 && (
        <>
          <h3>{s.settings}</h3>
          <p className="muted small">{s.settingsHint}</p>
          <ul className="plain stack">
            {settings.map((setting) => {
              const value = settingInDefinition(setting, def);
              return (
                <li key={setting.id}>
                  {setting.kind === 'switch' ? (
                    <label className="check">
                      <input
                        type="checkbox"
                        name={`setting-${setting.id}`}
                        checked={value === 'on'}
                        disabled={busy || !onSetting}
                        onChange={(e) => onSetting?.(setting.id, e.target.checked ? 'on' : 'off')}
                      />
                      <span>
                        <strong>{setting.title}</strong>
                        <span className="muted small block">{setting.blurb}</span>
                      </span>
                    </label>
                  ) : (
                    <label>
                      <strong>{setting.title}</strong>
                      <select
                        name={`setting-${setting.id}`}
                        value={value}
                        disabled={busy || !onSetting}
                        onChange={(e) => onSetting?.(setting.id, e.target.value)}
                      >
                        {setting.values.map((v) => (
                          <option key={v} value={v}>
                            {(setting.valueTitles[v] ?? v) +
                              (v === setting.default ? ` ${s.byDefault}` : '')}
                          </option>
                        ))}
                      </select>
                      <span className="muted small block">{setting.blurb}</span>
                    </label>
                  )}
                </li>
              );
            })}
          </ul>
          {busy && own.length === 0 && (
            <p className="muted small" role="status">
              {s.regenerating}
            </p>
          )}
        </>
      )}

      <h3>{s.phases}</h3>
      {phases.length === 0 ? (
        <p className="muted small">{s.none}</p>
      ) : (
        <ol className="script-phases">
          {phases.map((p) => (
            <li key={p.id}>
              <strong>{p.title || p.id}</strong>
              <span className="small block">{whenWords(p.when, def)}</span>
              {p.after && p.after.length > 0 && (
                <span className="muted small block">
                  {s.after(
                    p.after.map((id) => phases.find((x) => x.id === id)?.title || id).join(', '),
                  )}
                </span>
              )}
              {(p.do ?? []).map((entry, i) => (
                <span key={i} className="muted small block">
                  {doWords(entry, def)}
                </span>
              ))}
            </li>
          ))}
        </ol>
      )}

      <h3>{s.places}</h3>
      {(script.places ?? []).length === 0 ? (
        <p className="muted small">{s.none}</p>
      ) : (
        <ul className="plain">
          {(script.places ?? []).map((p) => (
            <li key={p.id} className="row">
              <span className="small grow">
                {placeWords(p)}
                {p.alternativeOf && <span className="muted block">{s.alternative}</span>}
              </span>
              {onMove && !p.alternativeOf && (
                <button
                  type="button"
                  className="small"
                  disabled={busy}
                  aria-label={s.moveAria(placeWords(p))}
                  onClick={() => onMove(p.id, placeWords(p))}
                >
                  {s.move}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {onMove && points.length > 0 && (
        <>
          <h3>{s.points}</h3>
          <ul className="plain">
            {points.map((id) => (
              <li key={id} className="row">
                <span className="small grow">{s.pointNames[id]}</span>
                <button
                  type="button"
                  className="small"
                  disabled={busy}
                  aria-label={s.moveAria(s.pointNames[id])}
                  onClick={() => onMove(id, s.pointNames[id])}
                >
                  {s.move}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      <h3>{s.scenes}</h3>
      {(script.scenes ?? []).length === 0 ? (
        <p className="muted small">{s.none}</p>
      ) : (
        <ul className="plain">
          {(script.scenes ?? []).map((scene) => (
            <li key={scene.id} className="small">
              <strong>{scene.label || scene.id}</strong>
              {scene.place && <> · {s.sceneAt(scene.place)}</>}
              {scene.forces && scene.forces.length > 0 && (
                <> · {s.sceneForces(scene.forces.length)}</>
              )}
            </li>
          ))}
        </ul>
      )}

      <h3>{s.forces}</h3>
      {forces.length === 0 ? (
        <p className="muted small">{s.none}</p>
      ) : (
        <ul className="plain">
          {forces.map((f) => (
            <li key={f.id} className="row">
              <span className="small">{f.label || f.id}</span>
              {f.scene && <span className="chip">{s.forceScene(sceneLabel(f.scene, script))}</span>}
              {f.posture && (
                <span className="chip">{s.forcePosture(s.postures[f.posture] ?? f.posture)}</span>
              )}
              {f.post && <span className="chip">{s.forcePost(f.post)}</span>}
            </li>
          ))}
        </ul>
      )}

      {people.length > 0 && (
        <>
          <h3>{s.cast}</h3>
          <ul className="plain">
            {people.map((c) => (
              <li key={c.id} className="row">
                <span className="small">{c.name}</span>
                {(c.scene || c.sceneRole) && (
                  <span className="chip">
                    {s.castScene(
                      [c.scene && sceneLabel(c.scene, script), c.sceneRole]
                        .filter(Boolean)
                        .join(' · '),
                    )}
                  </span>
                )}
                {c.mechanic && <span className="chip">{s.castMechanic(c.mechanic)}</span>}
              </li>
            ))}
          </ul>
        </>
      )}

      <h3>{s.mechanics}</h3>
      {(script.mechanicInstances ?? []).length === 0 ? (
        <p className="muted small">{s.none}</p>
      ) : (
        <ul className="plain">
          {(script.mechanicInstances ?? []).map((m) => (
            <li key={m.id} className="small">
              {s.mechanicNames[m.use] ?? m.use}
              {m.scene && (
                <span className="muted"> {s.mechanicAt(sceneLabel(m.scene, script))}</span>
              )}
            </li>
          ))}
        </ul>
      )}

      <h3>{s.clock}</h3>
      {ownClock ? (
        <>
          <p className="small">{s.ownClock}</p>
          <ul className="plain">
            {(script.clock?.marks ?? []).map((m) => (
              <li key={m.id} className="small">
                {s.mark(m.id, m.at ?? '')}
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="small">{limit !== null ? s.genericClock(limit) : s.none}</p>
      )}

      <h3>{s.end}</h3>
      {(script.end?.outcomes ?? []).length > 0 ? (
        <ul className="plain">
          {(script.end?.outcomes ?? []).map((o, i) => (
            <li key={i} className="small">
              {s.outcomeWhen(s.outcomes[o.outcome] ?? o.outcome, whenWords(o.when, def))}
            </li>
          ))}
        </ul>
      ) : (
        <p className="small">{s.genericEnd}</p>
      )}
      {(script.end?.report ?? []).length > 0 && (
        <p className="muted small">{s.report((script.end?.report ?? []).join(', '))}</p>
      )}
    </section>
  );
}
